import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  BatchRecordFillingBatch,
  BatchRecordFillingBatchesState,
  CreateBatchRecordFillingBatchRequest
} from '../../types/batchRecordFillingBatch';
import { batchRecordFillingBatchApiService } from '../../services/batchRecordFillingBatchApiService';

// Async thunks

/**
 * Fetch all batch record filling batches from the API
 */
export const fetchAllBatchRecordFillingBatches = createAsyncThunk(
  'batchRecordFillingBatches/fetchAll',
  async () => {
    return await batchRecordFillingBatchApiService.fetchAllBatchRecordFillingBatches();
  }
);

/**
 * Fetch batch record filling batches for a specific batch record
 */
export const fetchBatchRecordFillingBatchesByBatchRecordId = createAsyncThunk(
  'batchRecordFillingBatches/fetchByBatchRecordId',
  async (batchRecordId: string) => {
    return await batchRecordFillingBatchApiService.fetchBatchRecordFillingBatchesByBatchRecordId(batchRecordId);
  }
);

/**
 * Create a new batch record filling batch
 */
export const createBatchRecordFillingBatch = createAsyncThunk(
  'batchRecordFillingBatches/create',
  async (data: CreateBatchRecordFillingBatchRequest) => {
    return await batchRecordFillingBatchApiService.createBatchRecordFillingBatch(data);
  }
);

/**
 * Delete a batch record filling batch
 */
export const deleteBatchRecordFillingBatch = createAsyncThunk(
  'batchRecordFillingBatches/delete',
  async (id: string) => {
    await batchRecordFillingBatchApiService.deleteBatchRecordFillingBatch(id);
    return id;
  }
);

// Initial state
const initialState: BatchRecordFillingBatchesState = {
  items: [],
  loading: false,
  error: null,
};

const batchRecordFillingBatchSlice = createSlice({
  name: 'batchRecordFillingBatches',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch all batch record filling batches
    builder
      .addCase(fetchAllBatchRecordFillingBatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllBatchRecordFillingBatches.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchAllBatchRecordFillingBatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch batch record filling batches';
      });

    // Fetch by batch record ID
    builder
      .addCase(fetchBatchRecordFillingBatchesByBatchRecordId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBatchRecordFillingBatchesByBatchRecordId.fulfilled, (state, action) => {
        state.loading = false;
        // Replace items for this batch record
        const batchRecordId = action.payload[0]?.batchRecordId;
        if (batchRecordId) {
          state.items = [
            ...state.items.filter(i => i.batchRecordId !== batchRecordId),
            ...action.payload
          ];
        }
      })
      .addCase(fetchBatchRecordFillingBatchesByBatchRecordId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch batch record filling batches';
      });

    // Create batch record filling batch
    builder
      .addCase(createBatchRecordFillingBatch.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(createBatchRecordFillingBatch.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create batch record filling batch';
      });

    // Delete batch record filling batch
    builder
      .addCase(deleteBatchRecordFillingBatch.fulfilled, (state, action) => {
        state.items = state.items.filter(i => i.id !== action.payload);
      })
      .addCase(deleteBatchRecordFillingBatch.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete batch record filling batch';
      });
  }
});

export const { clearError } = batchRecordFillingBatchSlice.actions;
export default batchRecordFillingBatchSlice.reducer;
